import { isDefined } from '@sgme/fp';
import type { FieldMetadata } from 'models/forms/fields';
import type {
  SingleUnderlyingFieldMetadata,
  SingleUnderlyingSearchEditableFieldMetadata,
} from 'models/forms/fields/SingleUnderlyingSearchEditableFieldMetadata';
import type { UnderlyingsSearchFieldMetadata } from 'models/forms/fields/underlyings-search';
import type { UnderlyingsSearchEditableFieldMetadata } from 'models/forms/fields/UnderlyingsSearchEditableFieldMetadata';
import type { Underlying } from 'models/underlying';
import type { UnderlyingStrike } from 'models/underlying/base';

import { asGridRowsOrSingle, createGridCellExtractor } from '../gridExtractor';

const STRIKE_MODE_COLUMN_ID = 'UnderlyingStrikeMode';
const SPOT_COLUMN_ID = 'UnderlyingSpot';
const STRIKE_DATE_COLUMN_ID = 'UnderlyingStrikeDate';
const CUTOFF_COLUMN_ID = 'CutOff';

const COMMODITY_FAMILY_ASSETS = new Set([
  'Commodity',
  'Future',
  'Futures',
  'Precious Metal',
  'Precious Metals',
  'PreciousMetal',
]);

type WithStrike = { strike?: UnderlyingStrike };
type WithCutOff = { cutOff?: string };

type UnderlyingsMetadata =
  | UnderlyingsSearchEditableFieldMetadata
  | SingleUnderlyingSearchEditableFieldMetadata
  | SingleUnderlyingFieldMetadata
  | UnderlyingsSearchFieldMetadata;

export function isUnderlyingsMetadata(meta: FieldMetadata): meta is UnderlyingsMetadata {
  return (
    meta.type === 'SearchEditableField' ||
    meta.type === 'SingleSearchEditableField' ||
    meta.type === 'SingleUnderlyingField' ||
    meta.type === 'UnderlyingsSearch'
  );
}

function isCommodityFamilyAsset(asset?: string): boolean {
  return isDefined(asset) && COMMODITY_FAMILY_ASSETS.has(asset);
}

function hasStrike(row?: Underlying): row is Underlying & WithStrike {
  return isDefined(row) && row.hasOwnProperty('strike');
}

function hasCutOff(row?: Underlying): row is Underlying & WithCutOff {
  return isDefined(row) && row.hasOwnProperty('cutOff');
}

const getStrike = (row?: Underlying) => (hasStrike(row) ? row.strike : undefined);

const getCutOff = (row?: Underlying) => (hasCutOff(row) ? row.cutOff : undefined);

const isStrikeVisible = (row?: Underlying) => isDefined(getStrike(row));

const isStrikeColumnVisible = (row?: Underlying) =>
  isStrikeVisible(row) && (row?.asset === 'Equity' || isCommodityFamilyAsset(row?.asset));

export const underlyingsCellExtractor = createGridCellExtractor({
  isHandled: isUnderlyingsMetadata,
  getRows: asGridRowsOrSingle<Underlying>,
  includeParentFieldOnRowChange: true,
  getRowIdentity: row => row.id,
  getColumns: meta => [
    {
      id: STRIKE_MODE_COLUMN_ID,
      isVisible: isStrikeColumnVisible,
      includeOnRowChange: true,
      getValue: row => getStrike(row)?.strikeMode,
    },
    {
      id: SPOT_COLUMN_ID,
      isVisible: row => row?.asset === 'Equity' && isStrikeVisible(row),
      includeOnRowChange: true,
      getValue: row => getStrike(row)?.strikeValue,
    },
    {
      id: STRIKE_DATE_COLUMN_ID,
      isVisible: isStrikeColumnVisible,
      includeOnRowChange: true,
      getValue: row => getStrike(row)?.strikeDate,
    },
    {
      id: CUTOFF_COLUMN_ID,
      isVisible: row => meta.hasCutOff && row?.asset === 'Forex',
      includeOnRowChange: true,
      getValue: getCutOff,
    },
  ],
});
